/* eslint no-mixed-operators: ["error", {"allowSamePrecedence": true}] */
import { pipe, Option } from 'effect';
import { selector } from '@/lib/selectors';
import { Set, Map } from 'immutable';
import { getRowByNumber, jeSorterCb } from '@/domain/journal/helper';
import { mismatchedCellsAdapter } from '@/domain/journal/mismatchedCells';
import { parseMatchedLines } from '@/domain/journal/schemas';
import { applyFieldRearrangement } from '@/domain/journal/fieldRearrangementTransform';
import {
  fieldRearrangementPositionsSelector,
  fieldRearrangementHiddenSelector,
  fieldRearrangementShownSelector,
  fieldRearrangementLockedSelector,
} from '@/domain/jeFieldRearrangement/selectors';

import { denormalize } from '@/domain/journal/normalizer';

export const journal = (state) => state.journal;
export const journalSelector = selector(journal, (j) => denormalize(j).entry.sort(jeSorterCb));
export const jeNameSelector = selector(journal, (j) => j.jeName);
export const jeTypeSelector = selector(journal, (j) => j.jeType);
export const jeInvoiceTypeSelector = selector(journal, (j) => j.jeInvoiceType);
// Canonical currency identifier of the JE — top-level `jeCurrency` on the API
// response (mirrors `jeInvoiceType`). Used to detect currency changes that must
// invalidate the textract field-binding menu cache (DA-19222).
export const jeCurrencySelector = selector(journal, (j) => j.jeCurrency);
const transactionTypeFieldSelector = selector(journal, (j) => j.transactionTypeField);
export const transactionTypeNameSelector = selector(journalSelector, transactionTypeFieldSelector, (entry, fieldName) =>
  pipe(
    Option.fromNullable(fieldName),
    Option.flatMap((name) => Option.fromNullable(entry.find((cell) => cell.name === name))),
    Option.map((cell) => cell.cellSet),
    Option.flatMap(Option.fromNullable),
    Option.map((cs) => String(cs.display ?? cs.value ?? '')),
    Option.getOrElse(() => ''),
  ),
);
export const isJeLayoutRTLSelector = selector(journal, (j) => j.jeLayout === 'rtl');
export const isStatementSelector = selector(jeTypeSelector, (t) => ['bank_statement', 'reconcile'].includes(t));
export const journalEntryHasUserChangesSelector = selector(journal, (j) => j.wasFixed);

export const headerLinesNumberSelector = selector(journal, (j) => j.headerLinesNumber);
export const lineItemsSelector = selector(journal, (j) => j.line_items);

export const settingsSelector = selector(journal, (j) => j.settings);
export const settingsEntriesSelector = selector(journalSelector, settingsSelector, getRowByNumber);
export const linesSelector = selector(journal, (j) => j.lines);
export const initialHiddenColumnsSelector = selector(journal, (j) => j.initial_hidden_columns);

export const gridEntriesSelector = selector(journalSelector, linesSelector, getRowByNumber);

const summarySelector = selector(journal, (j) => j.summary);
export const summaryEntriesSelector = selector(journalSelector, summarySelector, getRowByNumber);
export const journalWarningsSelector = selector(journal, (j) => j.messages);
export const journalPublicationWarningsSelector = selector(journal, (j) => j.publicationWarnings);

export const errorFromErpSelector = selector(journal, (j) => j.errorFromErp);
export const lineCountSelector = selector(journal, (j) => j.lineCount);
export const selectedRowSelector = selector(journal, (j) => j.selectedRow);
export const reconcileColumnSelector = selector(journal, (j) => j.reconcileColumn);
export const pinnedColumnSelector = selector(journal, (j) => j.pinnedColumn);
export const notHiddenColumnsSelector = selector(journal, (j) => j.notHiddenColumns);
export const amountColumnSelector = selector(journal, (j) => j.amountColumn);
export const rowMessagesSelector = selector(journal, (j) => j.rowMessages);
export const jeGridLineColumnsDictionarySelector = selector(journal, (j) => j.jeGridLineColumnsDictionary);

const paginationSelector = selector(journal, (j) => j.paginationList);
export const paginationListSelector = selector(paginationSelector, (p) => p.list);
export const paginationListPageTokenSelector = selector(paginationSelector, (p) => p.pageToken);
const cachedPaginationListsSelector = selector(paginationSelector, (p) => p.cachedList);
export const getCachedPaginationListSelector = selector(cachedPaginationListsSelector, (cl) => (id) => cl.get(id));

const journalPaginationListsSelector = selector(journalSelector, (j) =>
  j.filter((e) => e.type === 'paginated_list' || e.type === 'paginated_multiselect_list'),
);

export const cachedPaginationListsIncludeIdsSelector = selector(cachedPaginationListsSelector, (cl) =>
  cl.reduce((a, v) => [...a, v.include_id], []),
);

export const journalNotCachedPaginationListsSelector = selector(
  journalPaginationListsSelector,
  cachedPaginationListsIncludeIdsSelector,
  (entries, cachedIds) => entries.filter((e) => e.include_id && !cachedIds.includes(e.include_id)),
);

export const journalSettings = (state) => state.journalSettings;
export const journalSettingsPinnedColsSelector = selector(journalSettings, (j) => j.pinnedCols);
export const journalSettingsDisabledColsSelector = selector(journalSettings, (j) => j.disabledCols);
export const journalSettingsOrderedColsSelector = selector(journalSettings, (j) => j.orderedCols);

export const jeMatchedLinesSelector = selector(journal, (j) => j.matchedLines);

/**
 * Grid address -> the document's value, for every cell that disagrees with it.
 *
 * Derived rather than stored: the store keeps the payload's own shape, and translating it
 * into the grid's coordinates is a view concern. Deriving it here also makes it impossible
 * for the cell highlight and the line highlight to be computed from different results.
 */
export const jeMismatchedCellsSelector = selector(
  jeMatchedLinesSelector,
  jeGridLineColumnsDictionarySelector,
  headerLinesNumberSelector,
  (matchedLines, columnsDictionary, headerLinesNumber) =>
    // Re-parsed rather than cast: the payload's type does not survive the store's
    // fromJS/toJS round-trip, and the schema is the only thing that can hand it back.
    mismatchedCellsAdapter(parseMatchedLines(matchedLines.toJS()), columnsDictionary, headerLinesNumber),
);

export const jeMatchedLineNumbersSelector = selector(jeMatchedLinesSelector, (lines) => {
  const res = lines
    .reduce(
      (acc, matchedLines, lineItemIndex) =>
        acc.set(
          lineItemIndex,
          matchedLines.map((line) => line.get('line', null)),
        ),
      new Map(),
    )
    .filter((list) => list && list.size > 0);
  return res;
});

// returns absolute line numbers e.g. 12,13,16
export const jeUnmatchedLineNumbersSelector = selector(
  jeMatchedLinesSelector,
  lineItemsSelector,
  (matchedLines, lineItems) => {
    const matchedLineNumbers = matchedLines
      .entrySeq()
      .flatMap(([key, items]) =>
        items.filter((item) => ['match', 'partial_match'].includes(item.get('status'))).map(() => parseInt(key, 10)),
      )
      .toSet()
      .toArray();

    // select only line items that are NOT at the matched indices (convert 1-based to 0-based)
    const unmatchedLineNumbers = lineItems.toArray().filter((_, index) => !matchedLineNumbers.includes(index + 1));

    return Set(unmatchedLineNumbers);
  },
);

export const isJELoadingSelector = selector(journal, (j) => j.loading);
export const isJEEditingSelector = selector(journal, (j) => j.editing);

export const textractForVendorEnabledSelector = selector(journal, (j) => j.textractForVendorEnabled);

/**
 * Settings entries with field rearrangement config applied.
 * Used by JEHeadersTable for the regular view: rearranged cells appear at their
 * target positions; hidden cells are removed from the OrderedMap (keys absent,
 * not null placeholders).
 */
export const rearrangedSettingsSelector = selector(
  settingsEntriesSelector,
  fieldRearrangementPositionsSelector,
  fieldRearrangementHiddenSelector,
  fieldRearrangementShownSelector,
  fieldRearrangementLockedSelector,
  applyFieldRearrangement,
);
